import React from 'react';
import { useHistory } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import { Button, Modal, Row, Space } from 'antd';
import {
  ExclamationCircleOutlined,
  PlusOutlined,
  LockOutlined,
  SearchOutlined,
  SelectOutlined,
  SettingOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { PatternCodeType } from 'components/Admin/PatternCode';
import {
  Label,
  Loading,
  ScreenMask,
  SetDefaultWarehouseDialog,
  SuccessButton,
  message,
} from 'components/common';
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  COMMON_MSG_DEFAULT_DISPLAY_DURATION,
  LOADING_ICON_SIZE1,
  LS_DEFAULT_WAREHOUSE_KEY,
  PoRecvStorageKey,
  SMART_PO_RECV_LIST_REQ_SIZE,
} from 'constants/config';
import { RootState } from 'reducers';
import { fetchPoLineList, fetchPoList, receive } from 'services/purchaseOrder';
import { fetchWarehouseLocationList } from 'services/warehouse';
import { getStorage, getWarehouseCodeFromCache } from 'utils';
import {
  GreenColor,
  SmartBackButton,
  SmartBlock,
  SmartButton,
  SmartNumberInput,
  SmartScanner,
  SmartScannerInput,
  SmartFormLabel,
  SmartSpace,
  playSound,
} from '../SmartComponent';
import CodeInputDialog from './CodeInputDialog';
import CreateLotDrawer from './CreateLotDrawer';
import PatternCodeSelector from './PatternCodeSelector';
import RecvItemList from './RecvItemList';
import SearchLotDrawer from './SearchLotDrawer';
import UomSelector, { checkoutUomBySkuAndCode } from './UomSelector';
import {
  DefaultScanRecvRatio,
  defaultUom,
  ScanRecvCountType,
  ScanRecvLotPolicy,
  ScanRecvRatioType,
  PoRecvActionType as PRAction,
  //PoRecvStep,
  initialState,
  isValidStep,
  reducer,
} from './context';
import {
  clearMatchedPatternList,
  extractPatternCodeList,
  findMatchProductCode,
  getDefaultLocation,
  getItemReceivingMaxQty,
  getMatchItemReceivingQty,
  getMatchedPatternCode,
  isAnyPatternMatched,
  isCodeMatchLine,
  isLotAvailableOnItem,
  isPatternCodeEnabled,
  setDefaultLocation,
} from '.';

const getStateFromStorage = () => {
  const state: StringKAnyVPair = {};
  const storage = getStorage('session');

  const scanRecvCountType = storage.getItem(PoRecvStorageKey.ScanRecvCountType);
  if (scanRecvCountType !== null && !isNaN(Number(scanRecvCountType))) state.scanRecvCountType = Number(scanRecvCountType);
  const scanRecvLotPolicy = storage.getItem(PoRecvStorageKey.ScanRecvLotPolicy);
  if (scanRecvLotPolicy !== null && !isNaN(Number(scanRecvLotPolicy))) state.scanRecvLotPolicy = Number(scanRecvLotPolicy);
  const scanRecvLotUniNum = storage.getItem(PoRecvStorageKey.ScanRecvLotUniNum);
  if (scanRecvLotUniNum !== null) state.scanRecvLotUniNum = scanRecvLotUniNum;
  const scanRecvRatio = storage.getItem(PoRecvStorageKey.ScanRecvRatio);
  if (scanRecvRatio !== null && !isNaN(Number(scanRecvRatio))) state.scanRecvRatio = Number(scanRecvRatio);
  const scanRecvRatioType = storage.getItem(PoRecvStorageKey.ScanRecvRatioType);
  if (scanRecvRatioType !== null && !isNaN(Number(scanRecvRatioType))) state.scanRecvRatioType = Number(scanRecvRatioType);

  return state;
};

const QuickReceive = () => {
  const { useReducer, useRef, useState } = React;
  const [currentDefaultLocationVersion, setCurrentDefaultLocationVersion] = useState(0);
  const [currentItem, setCurrentItem] = useState<StringKAnyVPair>({});
  const [currentItemCode, setCurrentItemCode] = useState('');
  const [currentItemLocked, setCurrentItemLocked] = useState(true);
  const [currentItemRecvQty, setCurrentItemRecvQty] = useState<number>();
  const [currentLotCode, setCurrentLotCode] = useState('');
  const [currentLotLocked, setCurrentLotLocked] = useState(true);
  const [currentPatternLocationVersion, setCurrentPatternLocationVersion] = useState(0);
  const [currentPatternPoVersion, setCurrentPatternPoVersion] = useState(0);
  const [currentPoFocusVersion, setCurrentPoFocusVersion] = useState(0);
  const [defaultLocationDialogVisible, setDefaultLocationDialogVisible] = useState(false);
  const [defaultWarehouseVisible, setDefaultWarehouseVisible] = useState(false);
  const [dialogClosable, setDialogClosable] = React.useState(true);
  const [inited, setInited] = useState(false);
  const [isCreateLotDrawerOpen, setIsCreateLotDrawerOpen] = useState(false);
  const [isDefaultLocationRequired, setIsDefaultLocationRequired] = useState(false);
  const [isSearchLotDrawerOpen, setIsSearchLotDrawerOpen] = useState(false);
  const [itemRecvInputVersion, setItemRecvInputVersion] = useState(0);
  const [itemScannerCode, setItemScannerCode] = useState('');
  const [itemScannerVersion, setItemScannerVersion] = useState(0);
  const [lastDefaultLocationVersion, setLastDefaultLocationVersion] = useState(0);
  const [lastPatternLocationVersion, setLastPatternLocationVersion] = useState(0);
  const [lastPatternPoVersion, setLastPatternPoVersion] = useState(0);
  const [lastPoFocusVersion, setLastPoFocusVersion] = useState(0);
  const [lastScanRecvVersion, setLastScanRecvVersion] = useState(0);
  const [locationScannerCode, setLocationScannerCode] = useState('');
  const [locationScannerVersion, setLocationScannerVersion] = useState(0);
  const [lotScannerVersion, setLotScannerVersion] = useState(0);
  const [patternCodeScannerVersion, setPatternCodeScannerVersion] = useState(0);
  const [patternCodeSelectorVisible, setPatternCodeSelectorVisible] = useState(false);
  const [poScannerCode, setPoScannerCode] = useState('');
  const [poScannerVersion, setPoScannerVersion] = useState(0);
  const [profileSettingInited, setProfileSettingInited] = useState(false);
  const [scanRecvVersion, setScanRecvVersion] = useState(0);
  const [uomSelectorVisible, setUomSelectorVisible] = useState(false);

  const gridDataPolicy = useRef<number>(0); // 0 is replace, 1 is append
  const gridPageIndex = useRef<number>(0);
  const gridPageSize = useRef<number>(SMART_PO_RECV_LIST_REQ_SIZE);

  const history = useHistory();
  const [state, dispatch] = useReducer(reducer, initialState(getStateFromStorage()));

  const userProfileSettings = useSelector(
    (state: RootState) => state.admin.userProfileSettings,
    shallowEqual
  );
  const warehouseList = useSelector(
    (state: RootState) => state.admin.warehouses,
    shallowEqual
  );

  const checkCurrentItem = (chk: StringKAnyVPair) => {
    //console.log('chk', currentItem, state);
    if (currentItem.sku) {
      if (chk.location) {
        const clc = chk.location.locationCode;

        //console.log('chk l', clc);
        if (typeof  clc === 'string' && clc !== currentItem.receivingLocationCode) {
          onClearScannedItem(true);
        }
      }

      if (chk.po) {
        const poId = chk.po.poId;

        //console.log('pp', poId, currentItem.poId);
        if (typeof poId === 'string' && poId !== currentItem.poId) {
          //onClearScannedItem(true);
          onClearPoScanner();
        }
      }
    }
  };

  // eslint-disable-next-line
  const checkPatternCode = (
    pct: PatternCodeType,
    item: StringKAnyVPair = currentItem,
  ) => {
    //const { dispatch, state } = props;

    if (isPatternCodeEnabled(state)) {
      switch(pct) {
        case PatternCodeType.LOT:
          if (isLotAvailableOnItem(state, item)) {
            const lot = getMatchedPatternCode(state.matchedPatternList, pct);

            if (lot) {
              setCurrentLotCode(lot);
              setLotScannerVersion(lotScannerVersion + 1);
            }
          }

          break;

        case PatternCodeType.Location:
          const location = getMatchedPatternCode(state.matchedPatternList, pct);
          //console.log('l', location);
          //if (location) handleLocationChange(location);
          if (location) onScanLocation(location);
          break;

        case PatternCodeType.Qty:
          const qty = getMatchedPatternCode(state.matchedPatternList, pct);

          //console.log('qty', qty, typeof qty);
          if (Number(qty) > 0) {
            setCurrentItemRecvQty(Number(qty));
            setItemRecvInputVersion(itemRecvInputVersion + 2);
            currentItemLocked && setCurrentItemLocked(false);
          }

          break;

        case PatternCodeType.SKU:
          const sku = getMatchedPatternCode(state.matchedPatternList, pct);
          //console.log('s', sku);
          setItemScannerCode(sku || '');

          if (sku) {
            onScanItem(sku);
          } else {
            setItemScannerVersion(itemScannerVersion + 1);
          }

          break;

        case PatternCodeType.UOM:
          if (state.enableUom) {
            const uomCode = getMatchedPatternCode(state.matchedPatternList, pct);

            //console.log('it', item, uomCode, state);
            if (uomCode) checkoutUomBySkuAndCode(item.sku, uomCode, state, dispatch);
          }

          break;
      }
    }
  };

  const closeCreateLotDrawer = () => {
    setIsCreateLotDrawerOpen(false);
  };

  const closeDefaultLocationDialog = () => {
    setDefaultLocationDialogVisible(false);
    tryFocusStartField();
  };

  const closePatternCodeSelector = () => {
    setPatternCodeSelectorVisible(false);
  };

  const closeSearchLotDrawer = () => {
    setIsSearchLotDrawerOpen(false);
  };

  const closeUomSelector = () => {
    setUomSelectorVisible(false);
  };

  const currentItemLockClass = () => {
    const cls = ['fs-20', 'h-50', 'w-48', 'p-4-12'];

    if (currentItemLocked) {
      cls.push('default-btn-danger');
    }

    return cls.join(' ');
  };

  const currentLotLockClass = () => {
    const cls = ['fs-20', 'h-50', 'w-48', 'p-4-12'];

    if (currentLotLocked) {
      cls.push('default-btn-danger');
    }

    return cls.join(' ');
  };

  // eslint-disable-next-line
  const doNextStepAfterPo = () => {
    const pct = PatternCodeType.Location;
    let location = state.defaultLocation;

    if (isPatternCodeEnabled(state)) {
      switch(pct) {
        case PatternCodeType.Location:
          location = getMatchedPatternCode(state.matchedPatternList, pct);
          //console.log('l', location);
          if (location) {}
          else {
            location = state.defaultLocation;
            // does there need to check the pattern sku
            if (isCurrentLocationSet() && state.recvItems.length > 0) {console.log('data dirty');}
          }
          break;
      }
    }

    if (location) {
      setLocationScannerCode(location);
      //setLocationScannerVersion(locationScannerVersion + 1);
      onScanLocation(location);
    } else {
      setTimeout(() => {
        setLocationScannerVersion(locationScannerVersion + 1);
      }, 0);
    }
  };

  const findMatchItem = (
    itemCode: string,
    locationCode: string,
    lotCode = currentLotCode,
  ) => {
    //const { state } = props;

    //props.state.recvItems.forEach((e: any) => console.log(isLotAvailableOnItem(props.state, e), e._receivingLot === lotCode, lotCode));
    state.recvItems.forEach((e: any) => console.log(isLotAvailableOnItem(state, e), e._receivingLot === lotCode, lotCode));
    //return state.recvItems.find((e: StringKAnyVPair) => e.receivingLocationCode === locationCode && isCodeMatchLine(itemCode, e) && (isLotAvailableOnItem(state, e) ? e._receivingLot === lotCode : true) && (state.enableUom ? (e.uom === state.uomInfo.uom && e.uomRate === state.uomInfo.uomRate) : true));
    return state.recvItems.find((e: StringKAnyVPair) => e.poNum === state.currentPo.poNum && e.receivingLocationCode === locationCode && isCodeMatchLine(itemCode, e) && (isLotAvailableOnItem(state, e) ? e._receivingLot === lotCode : true) && (state.enableUom ? isItemWithProductCodeUomOrStateUom(e, itemCode) : true));
  };

  const findMatchItem2 = (itemCode: string, locationCode: string) => {
    //const { state } = props;

    return state.recvItems.find((e: StringKAnyVPair) => e.poNum === state.currentPo.poNum && e.receivingLocationCode === locationCode && isCodeMatchLine(itemCode, e));
  };

  const findMatchItemOrLine = (itemCode: string, locationCode: string, lotCode = currentLotCode) => {
    return findMatchItem(itemCode, locationCode, lotCode) || findMatchLine(itemCode);
  };

  const findMatchLine = (code: string) => {
    //return props.state.poLines.find((l: StringKAnyVPair) => isCodeMatchLine(code, l));
    return state.poLines.find((l: StringKAnyVPair) => isCodeMatchLine(code, l));
  };

  const getDefaultItemRecvQty = (item: StringKAnyVPair) => {
    //const { state } = props;
    let qty = (currentItemRecvQty || 1) * DefaultScanRecvRatio;

    if (state.scanRecvCountType === ScanRecvCountType.Custom) {
      if (state.scanRecvRatioType === ScanRecvRatioType.FixedRatio) {
        qty = 1 * state.scanRecvRatio;
      } else if (item.poLineNum > 0 && state.scanRecvRatioType === ScanRecvRatioType.Unreceived) {
        qty = getItemReceivingMaxQty(state.recvItems, item) - (item._receivingQty || 0);
      }
    }

    return qty;
  };

  const getLatestFirstRecvItems = (
    items: StringKAnyVPair[],
    latestUuid: string,
  ) => {
    const idx = items.findIndex(e => e._recvUuid === latestUuid);
    const itemList = [ ...items ];
    let latest: StringKAnyVPair[] = [];

    if (idx > -1) latest = itemList.splice(idx, 1);

    return [ ...latest, ...itemList ];
  };

  const getMatchItemByCode = (
    itemCode: string,
    locationCode: string,
    lotCode = currentLotCode,
    uomInfo = state.uomInfo,
  ) => {
    //const { state } = props;
    const item = findMatchItemOrLine(itemCode, locationCode, lotCode);

    if (item) {
      if ((isCurrentLocationSet() && !isItemInCurrentLocation(item)) || (state.enableUom ? !isItemWithUom(item, uomInfo) : false)) {
        const ret: StringKAnyVPair = { ...item };

        //console.log('pi', state.currentPo);
        ret._baseQty = 0;
        ret._fulfillmentPoId = state.currentPo.fulfillmentPoId;
        ret._poNumber = state.currentPo.poNumber;
        ret._recvUuid = uuid();
        ret._receivingLot = '';
        ret._receivingQty = 0;
        //ret.receivingLocationCode = currentLocation.locationCode;
        ret.receivingLocationCode = state.currentLocation.locationCode;
        //ret.receivingLocationNum = currentLocation.locationNum;
        ret.receivingLocationNum = state.currentLocation.locationNum;
        delete ret.receivingQty;

        return ret;
      }
    }

    return item;
  };

  const getPoLines = async (id: string, warehouseCode: string) => {
    //const { dispatch, state } = props;
    const crossWarehouseReceive = state.crossWarehouseReceive;

    try {
      dispatch({ type: PRAction.SetLockScreen, params: true }); 

      const data = await fetchPoLineList(
        id,
        crossWarehouseReceive === '0' ? warehouseCode : undefined,
      );
      const lines = data.map((item) => {
        return {
          ...item,
          receivingLocationCode: undefined,
          receivingLocationNum: undefined,
          //receivingWarehouseCode: selectedWarehouse?.warehouseCode,
          receivingWarehouseCode: state.currentPo.warehouseCode,
          uom: item.uom || 'EA',
          uomRate: item.uomRate || 1,
          baseQty: 0,
          orderUom: item.uom,
          orderUomRate: item.uomRate,
          orderBaseQty: item.uomBaseQty,
          receivingQty: item.receivingQty || 0,
        };
      });
      dispatch({ type: PRAction.SetPoLines, params: lines });

      if (lines.length > 0) {
        /*if (isPatternCodeEnabled(state)) {
          if (getMatchedPatternCode(state.matchedPatternList, PatternCodeType.Location)) doNextStep();
        }*/
        //doNextStep();
        setCurrentPatternPoVersion(currentPatternPoVersion + 1);
      }
    } catch (err) {
      playSound('error');
      message.error({ content: `Fetched items in po error: ${err}` });
    } finally {
      dispatch({ type: PRAction.SetLockScreen, params: false }); 
    }
  };

  const getPoList = async (
    poNumberSearchValue: string,
    typeName = 'poNumber',
  ) => {
    //const { dispatch, state } = props;

    //setIsFetching(true);
    dispatch({ type: PRAction.SetLockScreen, params: true });
    try {
      const polist = await fetchPoList({
        warehouseCode: state.selectedWarehouse.warehouseCode,
        filterFieldName: typeName,
        filterKey: poNumberSearchValue || '',
        //poStatus: [1, 2].join(','),
        //poStatusNum: -1,
        sortBy: 'fulfillmentPoNum desc',
        skip: gridPageIndex.current * gridPageSize.current,
        limit: gridPageSize.current,
      });

      //if (typeof polist.count === 'number') setPoCount(polist.count);

      if (polist.data.length === 0) {
        playSound('error');

        if (poNumberSearchValue !== '') message.warning({ content: `No PO[${poNumberSearchValue}] matched`, duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION });
        /*if (isPatternCodeEnabled(state) && state.recvItems.length > 0 && props.gotoAnchor) {
          dispatch({ type: PRAction.SetMatchedPatternList, params: [] });
          props.gotoAnchor(PoRecvStep.RecvPo);
        }*/
        return;
      }

      const pos = polist.data.map((item) => {
        return { ...item, receivingQty: 0 };
      }); 
      dispatch({
        type: PRAction.SetPoList,
        params: gridDataPolicy.current === 0 ? pos : [ ...state.poList, ...pos ],
      });
      console.log('pl', polist.data);
      if (polist.data.length > 1) {
        const found = !!poNumberSearchValue && pos.find(e => e.poNumber === poNumberSearchValue);

        //setSelectPoDialogVisible(true);
        //setTimeout(adjustContentLayout, 0);
        //if (found) props.onSelectPo(found);
        found && onCheckoutPo(found);
      } else {
        //checkWarehouse(polist.data[0]);
        //props.onSelectPo(polist.data[0]);
        onCheckoutPo(polist.data[0]);
      }

      return polist.data;
    } catch (err) {
      console.log(err);
    } finally {
      //setIsFetching(false);
      dispatch({ type: PRAction.SetLockScreen, params: false });
    }
  };

  const getRecvItemGroupList = (
    items: StringKAnyVPair[],
  ): StringKAnyVPair[][] => {
    const dict: StringKAnyVPair = {};

    items.forEach(e => {
      if (!dict[e.poNum]) dict[e.poNum] = [];

      dict[e.poNum].push(e);
    });

    return Object.values(dict);
  };

  const isAutomaticSubmission = (
    item: StringKAnyVPair,
    ignore: PatternCodeType[] = [],
  ) => {
    let isPoSet = isCurrentPoSet();
    let isLocationSet = isCurrentLocationSet();
    let isItemSet = isItemScanned() && isItemInCurrentLocation(currentItem);
    let ret = (ignore.indexOf(PatternCodeType.PO) > -1 ? true : isPoSet)
        && (ignore.indexOf(PatternCodeType.Location) > -1 ? true : isLocationSet)
        && (ignore.indexOf(PatternCodeType.SKU) > -1 ? true : isItemSet);

    (!ret) && console.log('PO or Location or Item has not been entered yet');
    //console.log('r1', ret);
    if (ret && isLotScannable() && isLotAvailableOnItem(state, item)) {
      ret = !!currentLotCode;

      if (!ret && isPatternCodeEnabled(state)) {
        const lot = getMatchedPatternCode(state.matchedPatternList, PatternCodeType.LOT);
        //console.log('l', lot);
        ret = !!lot;
      }

      (!ret) && console.log('Lot can not be determined');
    }

    //console.log('r2', ret);
    if (ret && isUomEnable()) {
      ret = false;

      if (isPatternCodeEnabled(state)) {
        const uomCode = getMatchedPatternCode(state.matchedPatternList, PatternCodeType.UOM);
        //console.log('uc', uomCode);
        ret = !!uomCode;
      }

      (!ret) && console.log('No uom in pattern code');
    }

    //console.log('r3', ret);
    if (ret) {
      const qty = getDefaultItemRecvQty(item)
          || (isPatternCodeEnabled(state) ? Number(getMatchedPatternCode(state.matchedPatternList, PatternCodeType.Qty)) : 0);
      //console.log('qty', qty, currentItemRecvQty);
      ret = qty > 0;
      (!ret) && console.log('Qty can not be determined', qty);
    }
    //console.log('r->', ret);
    return ret;
  };

  const isCurrentLocationSet = () => {
    //return currentLocation.locationNum > 0;
    return state.currentLocation.locationNum > 0;
  };

  const isCurrentPoSet = () => {
    return state.currentPo.poNum > 0;
  };

  const isItemInCurrentLocation = (e: StringKAnyVPair) => {
    //const { state } = props;

    return isCurrentLocationSet()
        //&& e.receivingLocationCode === currentLocation.locationCode
        //&& e.receivingLocationNum === currentLocation.locationNum;
        && e.receivingLocationCode === state.currentLocation.locationCode
        && e.receivingLocationNum === state.currentLocation.locationNum;
  };

  const isItemScannable = () => {
    //return currentStep === RecvStep.ScanItem && isCurrentLocationSet();
    return isCurrentLocationSet();
  };

  const isItemScanned = () => {
    return currentItem.poLineNum > 0;
  };

  const isItemSubmittable = (
    checkItem = false,
    checkLotCode = false,
    checkQty = false,
  ) => {
    //let ret = currentStep === RecvStep.ScanItem && isItemScanned();
    //let ret = currentStep === RecvStep.ScanItem;
    let ret = isItemScannable();

    if (ret) {
      if (checkLotCode && isLotAvailableOnItem(state, currentItem)) ret = isLotScanned();
      if (ret && checkItem) ret = isItemScanned();
      if (ret && checkQty) ret = (currentItemRecvQty || 0) > 0;
    }

    return ret;
  };

  const isItemWithProductCodeUomOrStateUom = (
    item: StringKAnyVPair,
    itemCode: string,
  ) => {
    //const { state } = props;
    const pcItem = findMatchProductCode(itemCode, item);

    return pcItem ? isItemWithUom(item, pcItem) : isItemWithUom(item, state.uomInfo);
  };

  const isItemWithUom = (item: StringKAnyVPair, uom: StringKAnyVPair) => {
    return item.uom === uom.uom && item.uomRate === uom.uomRate;
  };

  const isLotReleased = () => {
    return isLotScannable(true) ? !currentLotLocked : false;
  };

  const isLotScanned = () => {
    return !!currentLotCode.trim();
  };

  const isLotScannable = (checkItem = false) => {
    //return currentStep === RecvStep.ScanItem && (checkItem ? (isItemScanned() && isLotAvailableOnItem(props.state, currentItem)) : props.state.enableLot);
    return isItemScannable() && (checkItem ? (isItemScanned() && isLotAvailableOnItem(state, currentItem)) : state.enableLot);
    //return currentStep === RecvStep.ScanItem && isLotAvailableOnItem(props.state, currentItem);
  };

  const isQtyChangeable = (checkItem = false) => {
    return state.scanRecvCountType === ScanRecvCountType.Default ? (checkItem ? (isItemScanned() && !currentItemLocked) : !currentItemLocked) : false;
  };

  const isUomEnable = (checkItem = false) => {
    //const { state } = props;

    //console.log('cc', currentItemCode, currentItem);
    //return currentStep === RecvStep.ScanItem && (checkItem ? (state.enableUom && isItemScanned() && !findMatchProductCode(currentItemCode, currentItem)) : state.enableUom);
    return isItemScannable() && (checkItem ? (state.enableUom && isItemScanned() && !findMatchProductCode(currentItemCode, currentItem)) : state.enableUom);
    //return currentStep === RecvStep.ScanItem && isLotAvailableOnItem(props.state, currentItem);
  };

  const onClearCurrentLocation = () => {
    dispatch({ type: PRAction.SetCurrentLocation, params: {} });
  };

  const onClearCurrentPo = () => {
    dispatch({ type: PRAction.SetCurrentPo, params: {} });
  };

  const onClearLotCode = () => {
    setCurrentLotCode('');
  };

  const onClearMatchedPatternList = () => {
    dispatch({ type: PRAction.SetMatchedPatternList, params: [] });
  };

  const onClearPoLines = () => {
    dispatch({ type: PRAction.SetPoLines, params: [] });
  };

  const onClearPoScanner = () => {
    onClearScannedItem(true);
    onClearScannedLocation(true);
    onClearScannedPo();
  };

  const onClearRecvItems = () => {
    dispatch({ type: PRAction.SetRecvItems, params: [] });
  };

  const onClearScannedItem = (checkLock = false) => {
    console.log('clear scanned item', checkLock);
    setCurrentItem({});
    setCurrentItemCode('');
    //setCurrentItemLocked(true);
    //setCurrentItemRecvQty(undefined);
    setDefaultItemRecvQty(checkLock);
    //if (!checkLock || (checkLock && !currentItemLocked)) setCurrentItemRecvQty(1);
    if (!checkLock || (checkLock && !currentLotLocked)) {
      setCurrentLotCode('');
      setLotScannerVersion(lotScannerVersion + 1);
    }

    setItemRecvInputVersion(itemRecvInputVersion + 1);
    setItemScannerCode('');
    setItemScannerVersion(itemScannerVersion + 1);
    dispatch({ type: PRAction.SetUomInfo, params: defaultUom });
  };

  const onClearScannedLocation = (useDefaultLocation = false) => {
    onClearCurrentLocation();
    setLocationScannerCode(useDefaultLocation ? state.defaultLocation : '');
    setLocationScannerVersion(locationScannerVersion + 1);
  };

  const onClearScannedPo = () => {
    onClearCurrentPo();
    setPoScannerCode('');
    setPoScannerVersion(poScannerVersion + 1);
  };

  const onClosePatternCodeSelectorWithoutSelection = () => {
    tryOpenDefaultLocationDialogWithoutPatternCode();
  };

  const onCheckoutPo = (po: StringKAnyVPair) => {
    console.log('sel', po);
    if (po.warehouseCode === state.selectedWarehouse.code) {
      //setCurrentPo(po);
      setPoScannerCode(po.poNumber);
      dispatch({ type: PRAction.SetCurrentPo, params: po });
      //gotoAnchor(PoRecvStep.PoInfo);
      //getPoLines(state.currentPo.poId, state.currentPo.warehouseCode);
      currentItem.sku && checkCurrentItem({po});
      getPoLines(po.poId, po.warehouseCode);
    } else {
      Modal.confirm({
        cancelText: 'No',
        centered: true,
        content: 'PO warehouse doesn’t match your default warehouse, do you want to switch the warehouse?',
        icon: <ExclamationCircleOutlined />,
        okText: 'Yes',
        onOk() {
          setDefaultWarehouseVisible(true);
        },
        //onCancel() { checkWarehouse(po); },
      });
    }
  };

  const onPageBack = () => {
    history.push('/smart/poreceive');
  };

  const onPatternCodeChange = (pc: string) => {
    const codeList: StringKAnyVPair[] = [];
    const sk = pc.trim();

    //console.log('patter code', pc);
    sk === '' && setPoScannerVersion(poScannerVersion + 1);
    sk !== '' && playSound('bell');

    if (sk && isPatternCodeEnabled(state)) {
      const pl = extractPatternCodeList(state, sk);
      const patternMatched = isAnyPatternMatched(pl || []);
      let po = '';

      //console.log('pl', pl);
      (pl || []).forEach(e => codeList.push(e));

      if (pl && patternMatched) po = getMatchedPatternCode(pl, PatternCodeType.PO);

      dispatch({ type: PRAction.SetMatchedPatternList, params: codeList });

      if (po) {
        setPoScannerCode(po);
        //setPoScannerVersion(poScannerVersion + 1);
        onScanPo(po);
      } else {
        message.warning({ content: 'No PO found', duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION });
        //return;
      }
    }
  };

  const onScanItem = (itemCode: string) => {
    //const { state } = props;
    const code = itemCode.trim();
    const line = findMatchLine(code);

    if (!line && isPatternCodeEnabled(state)) {
      const mSku = getMatchedPatternCode(state.matchedPatternList || [], PatternCodeType.SKU);

      console.log('ms', mSku, state);
      if (mSku !== code) {
        if (tryNextStepByPatternCode(code)) return;
      }
    }

    if (!isCurrentLocationSet()) {
      playSound('error');
      message.error({ content: 'Please scan a right location at first', duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION });
      return;
    }

    if (code !== currentItemCode) {
      //console.log('item', line, code);
      setCurrentItemCode(code);

      if (code !== itemScannerCode) {
        setItemScannerCode(code);
      }

      if (line) {
        //const item = getMatchItemByCode(code, currentLocation.locationCode);
        const item = getMatchItemByCode(code, state.currentLocation.locationCode);
        console.log('l', line, item);
        playSound('bell');
        setItemRecvInputVersion(itemRecvInputVersion + 1);

        if (item) {
          const prdCode = findMatchProductCode(code, item);
          const rQty = getDefaultItemRecvQty(item);
          let recvDirectly = false;
          let lotRecvable = false;

          if (rQty === 0) {
            playSound('error');
            Modal.error({
              title: 'Error',
              content: `The required quantity of [${code}] has been received`,
              okButtonProps: {
                //size: 'large',
              },
              onOk() {
                setCurrentItemCode('');
                setItemScannerCode('');
                setItemScannerVersion(itemScannerVersion > 0 ? 0 : 1);
              },
            });
            return;
          }

          if (isLotAvailableOnItem(state, item)) {
            switch (state.scanRecvLotPolicy) {
              case ScanRecvLotPolicy.AssignSameLot:
                setCurrentLotCode(state.scanRecvLotUniNum);
                //setLotCodeOnFormItem(item, props.state.scanRecvLotUniNum);
                setLotScannerVersion(lotScannerVersion + 1);

                //if (currentLotLocked && currentItemLocked) submitRecvForm();
                if (currentLotLocked && currentItemLocked) lotRecvable = true;
              break;

              case ScanRecvLotPolicy.InputManually:
                //console.log('hl', currentLotCode);
                if (currentLotLocked) {
                  //if (isLotScanned()) submitRecvForm();
                  if (isLotScanned()) lotRecvable = true;
                  else setCurrentLotLocked(false);
                } else {
                  if (!currentLotLocked) {
                    setCurrentLotCode('');
                    setLotScannerVersion(lotScannerVersion + 1);
                  }
                }
              break;
            }
          } else {
            if (currentItemLocked) {
              if (state.enableUom) {
                if (prdCode) recvDirectly = true;
              } else {
                recvDirectly = true;
              }
            }
          }

          //console.log('si', item._recvUuid, item, prdCode, recvDirectly);
          setCurrentItem(item);
          setCurrentItemRecvQty(rQty);
          trySetFormByItemCode(itemCode, item);

          checkPatternCode(PatternCodeType.LOT, item);
          checkPatternCode(PatternCodeType.UOM, item);
          checkPatternCode(PatternCodeType.Qty, item);

          if (!recvDirectly) {
            if (state.scanRecvCountType === ScanRecvCountType.Custom && !isLotAvailableOnItem(state, item)) {
              if (state.enableUom) {
                if (prdCode) recvDirectly = true;
              } else {
                recvDirectly = true;
              }
            }

            if (isLotAvailableOnItem(state, item)) {
              //console.log('-&->', recvDirectly, lotRecvable);
              if (!recvDirectly && lotRecvable) {
                if (state.enableUom) {
                  recvDirectly = !!prdCode;
                } else {
                  recvDirectly = true;
                }
              }
            }
          }

          (!recvDirectly) && (recvDirectly = isAutomaticSubmission(item, [PatternCodeType.SKU]));
          recvDirectly && setScanRecvVersion(scanRecvVersion + 1);
        }
      } else {
        playSound('error');
        message.error({
          content: code ? `[${code}] not found` : 'Please scan the item code',
          duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
        });
        setTimeout(() => {
          //setItemScannerCode('');
          setItemScannerVersion(itemScannerVersion + 1);
        }, 0);
      }
    } else {
      if (code === itemScannerCode) {
        playSound('error');
        message.error({ content: code ? `[${code}] not found` : 'Please scan the item code', duration: 2 });
        setTimeout(() => {
          setItemScannerCode('');
          setItemScannerVersion(itemScannerVersion + 1);
        }, 0);
      } else {
        setItemScannerCode(code);
        setItemScannerVersion(itemScannerVersion + 1);
      }
    }
  };

  const onScanLocation = async (str: string, evt?: React.KeyboardEvent<HTMLInputElement>) => {
    //const { dispatch, state } = props;
    const val = str.trim();

    if (val === '') return;

    if (isPatternCodeEnabled(state)) {
      //const ln = getMatchedPatternCode(state.matchedPatternList || [], PatternCodeType.Location);
      const ln = getMatchedPatternCode(extractPatternCodeList(state, val) || [], PatternCodeType.Location);

      console.log('->', ln, val, state.currentLocation);
      if (ln && ln !== val) {
        if (tryNextStepByPatternCode(val)) return;
      }
    }

    evt && evt.preventDefault();
    playSound('bell');
    // location should be put into state in order to improve efficiency
    dispatch({ type: PRAction.SetLockScreen, params: true }); 
    try {
      //console.log('w', state.selectedWarehouse);
      const res = await fetchWarehouseLocationList(
        state.selectedWarehouse.warehouseId,
        false,
        val,
        0,
      );

      if (Array.isArray(res)) {
        if (res.length === 1) {
          console.log('location', res[0]);
          //setCurrentLocation(res[0]);
          dispatch({ type: PRAction.SetCurrentLocation, params: res[0] });
          setLocationScannerCode(res[0].code);
          //setCurrentStep(RecvStep.ScanItem);
          currentItem.sku && checkCurrentItem({location: res[0]});

          if (isPatternCodeEnabled(state)) {
            setCurrentPatternLocationVersion(currentPatternLocationVersion + 1);
          } else {
            setItemScannerVersion(itemScannerVersion + 1);
          }
        } else {
          playSound('error');
          if (res.length === 0) {
            message.error(`Not found location [${val}]`);
            if (isPatternCodeEnabled(state) && isCurrentLocationSet() && state.recvItems.length > 0) {
              //setCurrentStep(RecvStep.ScanItem);
              onClearCurrentLocation();
            }
          } else {
            message.error(`Found ${res.length} location(s) for [${val}]`);
          }
        }
      } else {
        playSound('error');
        message.error(`Format error, not found location [${val}]`);
      }
    } catch(e) {
      playSound('error');
      message.error(`Fetched location information error: ${e}`);
    } finally {
      dispatch({ type: PRAction.SetLockScreen, params: false }); 
    }
  };

  const onScanLot = (lotStr: string) => {
    //const { state } = props;
    const lotCode = lotStr.trim();
    //const item = getMatchItemByCode(currentItemCode, currentLocation.locationCode, lotCode);
    const item = getMatchItemByCode(currentItemCode, state.currentLocation.locationCode, lotCode);

    //console.log('sl', item, item._recvUuid);
    if (item) {
      // note: the item is different from the item setted when scanning sku
      setCurrentItem(item);
      setCurrentItemRecvQty(getDefaultItemRecvQty(item));
      setLotCodeOnFormItem(item, lotCode);
      playSound('bell');

      //if (currentItemLocked) submitRecvForm();
    } else {
      playSound('error');
      message.error({ content: `LOT#[${lotCode}] not found` });
    }
  };

  const onScanPo = (poCode: string, evt?: React.KeyboardEvent<HTMLInputElement>) => {
    const sk = poCode.trim();

    playSound('bell');
    evt && evt.preventDefault();
    //setSearchPo(sk);
    if (sk === '') {
      setCurrentPoFocusVersion(currentPoFocusVersion + 1);
    } else {
      getPoList(sk);
    }
  };

  const onSelectPatternCode = (pat: StringKAnyVPair) => {
    const location = (pat.items || []).find((p: StringKAnyVPair) => p.valueType === PatternCodeType.Location);
    //console.log('p', location);
    !location && openDefaultLocationDialog();
  };

  const onSelectLot = (lot: StringKAnyVPair) => {
    //console.log('lot', lot);
    onScanLot(lot.lotNumber);
    setLotScannerVersion(lotScannerVersion + 1);
  };

  const onSelectUom = (uomInfo?: StringKAnyVPair) => {
    //const item = getMatchItemByCode(currentItemCode, currentLocation.locationCode, currentLotCode, uomInfo || props.state.uomInfo);
    const item = getMatchItemByCode(currentItemCode, state.currentLocation.locationCode, currentLotCode, uomInfo || state.uomInfo);

    if (item) {
      //console.log('su', uomInfo, item, item._recvUuid);
      setCurrentItem(item);
      playSound('bell');
    }
  };

  const onSetDefaultLocation = (location: string) => {
    const clc = state.currentLocation.locationCode;
    //const dlc = state.defaultLocation;
    //console.log('l', location, state);
    setIsDefaultLocationRequired(true);
    setDefaultLocation(dispatch, location);

    if (!clc) {
      setLocationScannerCode(location);
      setCurrentDefaultLocationVersion(currentDefaultLocationVersion + 1);
    } else {
      if (clc.toLowerCase() !== location.toLowerCase()) {
        message.info({
          content: 'Default location will tack effect next time',
          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
        });
      }
    }
  };

  const openCreateLotDrawer = () => {
    setIsCreateLotDrawerOpen(true);
  };

  const openDefaultLocationDialog = () => {
    setDefaultLocationDialogVisible(true);
  };

  const openPatternCodeSelector = () => {
    setPatternCodeSelectorVisible(true);
  };

  const openSearchLotDrawer = () => {
    setIsSearchLotDrawerOpen(true);
  };

  const openSetDefaultWarehouseDialog = (config: StringKAnyVPair) => {
    setDialogClosable(
      typeof config.closable === 'boolean' ? config.closable : true
    );
    setDefaultWarehouseVisible(true);
  };

  const openUomSelector = () => {
    console.log('u', currentItem);
    setUomSelectorVisible(true);
  };

  // eslint-disable-next-line
  const receiveItem = () => {
    //const { dispatch, state } = props;
    //const item = findMatchItem(currentItemCode, currentLocation.locationCode);
    const item = findMatchItem(currentItemCode, state.currentLocation.locationCode);
    const line = findMatchLine(currentItemCode);
    const nQty = getMatchItemReceivingQty(state.recvItems, currentItemCode);
    const recvQty = currentItemRecvQty || 0;
    const baseQty = state.uomInfo.uomRate * recvQty;
    let recvItems: StringKAnyVPair[] = [];
    console.log('ur', recvQty, baseQty);
    // check whether the Qty is invaild
    if (state.enablePOReceiveControl) {
      const maxQty = Math.round(line.poQty * (1 + state.poOverRecvPercent));

      //if (nQty + recvQty + line.humanReceiveQty > maxQty) {
      if (nQty + baseQty + line.humanReceiveQty > maxQty) {
        playSound('error');
        Modal.error({
          title: 'Error',
          content: `Cannot exceed total quatity[${maxQty}]`,
          okButtonProps: {
            //size: 'large',
          },
        });
        return;
      }
    }

    if (item) {
      //console.log('item', item, currentItemRecvQty);
      //currentItem._receivingQty = currentItem._receivingQty + currentItemRecvQty;
      item._receivingQty = item._receivingQty + recvQty;
      item._baseQty = item._baseQty + baseQty;
      //setRecvItems([...recvItems]);
      recvItems = getLatestFirstRecvItems([...state.recvItems], item._recvUuid);
      //dispatch({ type: PRAction.SetRecvItems, params: getLatestFirstRecvItems([...state.recvItems], item._recvUuid) });
      dispatch({ type: PRAction.SetRecvItems, params: [ ...recvItems ] });
    } else {
      currentItem._baseQty = baseQty;
      currentItem._receivingLot = currentLotCode;
      currentItem._receivingQty = recvQty;
      currentItem.uom = state.uomInfo.uom;
      currentItem.uomRate = state.uomInfo.uomRate;
      //setRecvItems([currentItem, ...recvItems]);
      recvItems = [currentItem, ...state.recvItems];
      //dispatch({ type: PRAction.SetRecvItems, params: [currentItem, ...state.recvItems] });
      dispatch({ type: PRAction.SetRecvItems, params: [ ...recvItems ] });
    }
    console.log('r->', item, currentItem._recvUuid, currentItem, line);

    if (line) {
      const qty = getMatchItemReceivingQty(state.recvItems, currentItemCode);

      //console.log('rqty', qty);
      //line.receivingQty = qty + (currentItemRecvQty || 0);
      line.receivingQty = qty + recvQty;
      line.baseQty = qty + baseQty;
      dispatch({ type: PRAction.SetPoLines, params: [...state.poLines] });
    }

    playSound('success');
    onClearScannedItem(true);
    onClearScannedLocation(true);
    onClearScannedPo();

    if (isPatternCodeEnabled(state)) {
      state.matchedPatternList.length > 0 && clearMatchedPatternList(dispatch);
    }
    // save the item directly
    saveReceivingItems(recvItems);
  };

  const receiveNextPOByPatternCode = (pc: string) => {
  };

  const saveReceivingItems = async (
    recvItems: StringKAnyVPair[] = state.recvItems,
  ) => {
    //const { dispatch, state } = props;
    //const recvItemGroups = getRecvItemGroupList(state.recvItems);
    const recvItemGroups = getRecvItemGroupList(recvItems);
    let saved = false;

    console.log(state);
    for (let i = 0; i < recvItemGroups.length; i++) {
      const g = recvItemGroups[i];

      dispatch({ type: PRAction.SetLockScreen, params: true }); 
      try {
        //const res = await receive(state.currentPo.fulfillmentPoId, {
        const res = await receive(g[0].poId, {
          WarehouseNum: state.selectedWarehouse.warehouseNum,
          //Items: state.recvItems.map((e: StringKAnyVPair) => ({
          // eslint-disable-next-line
          Items: g.map((e: StringKAnyVPair) => ({
            SKU: e.sku,
            FulfillmentPoLineNum: e.fulfillmentPoLineNum,
            //ReceiveQty: e.humanReceiveQty + e._receivingQty,
            ReceiveQty: e._receivingQty,
            LocationNum: e.receivingLocationNum,
            LotNumber: isLotAvailableOnItem(state, e) ? e._receivingLot : undefined,
            //ReceiptDate: moment(receiptDate).format('YYYY-MM-DD'),
            ReceiptDate: moment().format('YYYY-MM-DD'),
            Uom: e.uom,
            UomRate: e.uomRate,
          })),
        });

        if (res) {
          //setCurrentStep(RecvStep.ReceivedItem);
          saved = true;
          message.info({
            content: 'Saved successfully',
            duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
          });
          console.log('s', g[0].poId);
        }
      } catch(e) {
        message.error(`Received error: ${e}`);
      } finally {
        dispatch({ type: PRAction.SetLockScreen, params: false }); 
      }
    }

    if (saved) {
      onClearRecvItems();
      onClearPoLines();
      //if (isPatternCodeEnabled(state)) dispatch({ type: PRAction.SetMatchedPatternList, params: [] });
      isPatternCodeEnabled(state) && onClearMatchedPatternList();
      dispatch({ type: PRAction.SetSavedItems, params: [ ...recvItems ] });
      //tryFocusStartField();
      setCurrentDefaultLocationVersion(currentDefaultLocationVersion + 1);
      //setCurrentStep(RecvStep.ReceivedItem);
      //receiveNextPO();
    }
  };

  const searchLotButton = () => {
    return (
      <Button
        disabled={!isLotReleased()}
        onClick={openSearchLotDrawer}
        type="text"
      >
        <SearchOutlined />
      </Button>
    );
  };

  // eslint-disable-next-line
  const setDefaultItemRecvQty = (checkLock = false) => {
    //const { state } = props;
    let qty: any = currentItemRecvQty;

    if (state.scanRecvCountType === ScanRecvCountType.Custom) {
      if (state.scanRecvRatioType === ScanRecvRatioType.FixedRatio) {
        qty = 1 * state.scanRecvRatio;
      } else if (state.scanRecvRatioType === ScanRecvRatioType.Unreceived) {
        //qty = getItemReceivingMaxQty(state.recvItems, item) - (item._receivingQty || 0);
        qty = undefined;
      }
    } else {
      if (!checkLock || (checkLock && !currentItemLocked)) {
        qty= 1 * DefaultScanRecvRatio;
      }
    }
    //console.log(' -> ', state.scanRecvCountType === ScanRecvCountType.Custom, qty);

    setCurrentItemRecvQty(qty);
  };

  const setLotCodeOnFormItem = (item: StringKAnyVPair, lotCode: string) => {
    item._receivingLot = lotCode;
    setCurrentLotCode(lotCode);
  };

  const submitItemButton = () => {
    return (
      <SmartButton
        className="mb-0"
        disabled={!isItemSubmittable(true, true, true)}
        onClick={receiveItem}
        size="large"
        type="primary"
      >
        {/*Submit*/}
        Save
      </SmartButton>
    );
  };

  // eslint-disable-next-line
  const tryFocusStartField = () => {
    if (isPatternCodeEnabled(state)) {
      setPatternCodeScannerVersion(patternCodeScannerVersion + 1);
    } else {
      setPoScannerVersion(poScannerVersion + 1);
    }
  };

  const tryNextStepByPatternCode = (code: string) => {
    //const { dispatch, state } = props;
    const pl = extractPatternCodeList(state, code);
    const ln = getMatchedPatternCode(pl || [], PatternCodeType.Location);
    const po = getMatchedPatternCode(pl || [], PatternCodeType.PO);
    const sku = getMatchedPatternCode(pl || [], PatternCodeType.SKU);
    let ret = false;

    console.log('p', po, state.currentPo, state.currentLocation);
    if (po && po !== state.currentPo.poNumber) {
      receiveNextPOByPatternCode(code);
      ret = true;
    }

    //if (!ret && ln && ln !== currentLocation.locationCode) {
    if (!ret && ln) {
      if (ln === state.currentLocation.locationCode) {
        if (sku && isCurrentLocationSet()) {
          dispatch({ type: PRAction.SetMatchedPatternList, params: pl });
          setCurrentPatternLocationVersion(currentPatternLocationVersion + 1);
          //setCurrentStep(RecvStep.ScanItem);
          ret = true;
        }
      } else {
        if (state.currentPo.poNum > 0) {
          dispatch({ type: PRAction.SetMatchedPatternList, params: pl });
          //handleLocationChange(ln);
          onScanLocation(ln);
          ret = true;
        }
      }
    }

    if (!ret && sku && isCurrentLocationSet()) {
      if (state.currentPo.poNum > 0) {
        dispatch({ type: PRAction.SetMatchedPatternList, params: pl });
        //onScanItem(sku);
        setCurrentPatternLocationVersion(currentPatternLocationVersion + 1);
        ret = true;
      }
    }

    return ret;
  };

  const trySetFormByItemCode = (
    itemCode: string,
    curItem: StringKAnyVPair | undefined = undefined,
  ) => {
    //const { dispatch, state } = props;
    //const item = findMatchItem2(itemCode, currentLocation.locationCode);
    const item = findMatchItem2(itemCode, state.currentLocation.locationCode);
    // console.log('i -->', item, itemCode);
    if (item) {
      if (isLotAvailableOnItem(state, item) && !currentLotLocked) {
        // lot was not set by outer logics
        if (!currentLotCode && item._receivingLot) {
          setCurrentLotCode(item._receivingLot);
        }
      }

      // can not use isUomEnable() here
      if (state.enableUom) {
        // whether need to add a lock
        if (isItemWithUom(state.uomInfo, defaultUom)) {
          //dispatch({ type: PRAction.SetUomInfo, params: { uom: item.uom, uomRate: item.uomRate } });
        }
      }
      // qty is not need to be the same as before
    }

    if (curItem) {
      if (state.enableUom) {
        const pcItem = findMatchProductCode(itemCode, curItem);
        //console.log('prd', pcItem);
        if (pcItem) {
          dispatch({ type: PRAction.SetUomInfo, params: { uom: pcItem.uom, uomRate: pcItem.uomRate } });
        }
      }
    }
  };

  // eslint-disable-next-line
  const tryOpenDefaultLocationDialogWithoutPatternCode = () => {
    !getDefaultLocation(state) && openDefaultLocationDialog();
  };

  const toggleCurrentItemLockState = () => {
    setCurrentItemLocked(!currentItemLocked);
  };

  const toggleCurrentLotLockState = () => {
    setCurrentLotLocked(!currentLotLocked);
  };

  const usePoQtyAsCurrentItemRecvQty = () => {
    const line = findMatchLine(currentItemCode);

    if (line) {
      const nQty = getMatchItemReceivingQty(state.recvItems, currentItemCode);
      const rQty = line.poQty - line.humanReceiveQty - nQty;
      //console.log('rqty', line, nQty, rQty);
      setCurrentItemRecvQty(rQty);
      setItemRecvInputVersion(itemRecvInputVersion + 1);
    }
  };

  const updateCurrentItemRecvQty = (
    qty: any,
    updateInput?: boolean,
  ) => {
    if (typeof qty === 'number') {
      //const { state } = props;

      if (state.enablePOReceiveControl) {
        const line = findMatchLine(currentItemCode);
        const nQty = getMatchItemReceivingQty(state.recvItems, currentItemCode);
        const maxQty = Math.round(line.poQty * (1 + state.poOverRecvPercent));

        if (nQty + qty + line.humanReceiveQty > maxQty) {
          playSound('error');
          Modal.error({
            title: 'Error',
            content: `Cannot exceed total quatity[${maxQty}]`,
            onOk() {
              setCurrentItemRecvQty(currentItemRecvQty);
              setItemRecvInputVersion(itemRecvInputVersion + 1);
            },
          });
        } else {
          setCurrentItemRecvQty(qty);
        }
      } else {
        setCurrentItemRecvQty(qty);
      }
    }

    if (updateInput) setItemRecvInputVersion(itemRecvInputVersion + 1);
  };

  React.useEffect(() => {
    if (!inited) {
      const whid = Number(localStorage.getItem(LS_DEFAULT_WAREHOUSE_KEY));

      if (!whid || (typeof whid === 'number' && !getWarehouseCodeFromCache(whid))) {
        openSetDefaultWarehouseDialog({ closable: false });
      }

      const wh = warehouseList.find(w => w.id === whid);

      if (wh) {
        dispatch({ type: PRAction.SetSelectedWarehouse, params: {...wh} });
      }

      //gotoAnchor(PoRecvStep.ScanPo);
      setInited(true);
    }

    if (userProfileSettings === null && !state.lockScreen) dispatch({ type: PRAction.SetLockScreen, params: true });

    if (userProfileSettings && !profileSettingInited) {
      const cwr = userProfileSettings.find(
        (e) => e.settingCode === 'CrossWarehouseReceive'
      );
      const enl = userProfileSettings.find((e) => e.settingCode === 'EnableLOT');
      const eor = userProfileSettings.find(
        (e) => e.settingCode === 'EnablePOOverReceive'
      );
      const ere = userProfileSettings.find(
        (e) => e.settingCode === 'EnablePOReceiveExtra'
      );
      const euo = userProfileSettings.find((e) => e.settingCode === 'EnableUOM');
      const mpc = userProfileSettings.find(e => e.settingCode === 'EnableMultiPurpostProductCode');
      const prp = userProfileSettings.find((e) => e.settingCode === 'POOverReceivePercentage');
      const tiz = userProfileSettings.find((e) => e.settingCode === 'TimeZone');

      //console.log('cw->', cwr, ere, eor, enl, euo, prp, mpc);
      setProfileSettingInited(true);
      dispatch({ type: PRAction.SetLockScreen, params: false });

      if (cwr) dispatch({ type: PRAction.SetCrossWarehouseReceive, params: cwr.settingValue });
      if (enl) dispatch({ type: PRAction.SetEnableLot, params: enl.settingValue === '1' });
      if (eor) dispatch({ type: PRAction.SetEnablePOReceiveControl, params: eor.settingValue === '1' });
      if (ere) dispatch({ type: PRAction.SetEnablePOReceiveExtra, params: ere.settingValue });
      if (euo) dispatch({ type: PRAction.SetEnableUom, params: euo.settingValue === '1' });
      if (tiz) dispatch({ type: PRAction.SetTimeZone, params: tiz.settingValue });

      if (mpc) {
        dispatch({ type: PRAction.SetEnableMultiPurposeCode, params: mpc.settingValue === '1' });

        if (mpc.settingValue === '1') openPatternCodeSelector();
      } else {
        tryOpenDefaultLocationDialogWithoutPatternCode();
      }

      if (prp) {
        const pcn = Number(prp.settingValue) ? Number(prp.settingValue) / 100 : 0;

        dispatch({ type: PRAction.SetPoOverRecvPercent, params: pcn });
      }
    }

    if (currentDefaultLocationVersion !== lastDefaultLocationVersion) {
      tryFocusStartField();
      setLastDefaultLocationVersion(currentDefaultLocationVersion);
    }

    if (currentPatternLocationVersion !== lastPatternLocationVersion) {
      checkPatternCode(PatternCodeType.SKU);
      setLastPatternLocationVersion(currentPatternLocationVersion);
    }

    if (currentPatternPoVersion !== lastPatternPoVersion) {
      doNextStepAfterPo();
      setLastPatternPoVersion(currentPatternPoVersion);
    }

    if (currentPoFocusVersion !== lastPoFocusVersion) {
      setPoScannerVersion(poScannerVersion + 1);
      setLastPoFocusVersion(currentPoFocusVersion);
    }

    if (scanRecvVersion !== lastScanRecvVersion) {
      if (!state.isCheckingUom) {
        //console.log('sv', /*itemReceived,*/ scanRecvVersion);
        receiveItem();
        //itemReceived = true;
        setLastScanRecvVersion(scanRecvVersion);
      }
    }

    const handleHashChange = () => {
      //const anchor = window.location.hash.trim().slice(1) || DefaultStep;
      const anchor = window.location.hash.trim().slice(1);

      if (isValidStep(anchor)) {
        //console.log('ch step', anchor);
        dispatch({ type: PRAction.SetRecvStep, params: anchor });
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [
    checkPatternCode,
    currentDefaultLocationVersion,
    currentPatternLocationVersion,
    currentPatternPoVersion,
    currentPoFocusVersion,
    doNextStepAfterPo,
    inited,
    lastDefaultLocationVersion,
    lastPatternLocationVersion,
    lastPatternPoVersion,
    lastPoFocusVersion,
    lastScanRecvVersion,
    poScannerVersion,
    profileSettingInited,
    receiveItem,
    scanRecvVersion,
    state,
    tryFocusStartField,
    tryOpenDefaultLocationDialogWithoutPatternCode,
    userProfileSettings,
    warehouseList,
  ]);

  return (<>
    <SmartSpace className="mb-5">
      {state.savedItems.length > 0 && (<>
        <Row className="mt-5">
          <Label theme={{fontSize: '1.3em'}}>Last Received Item</Label>
        </Row>
        {/*renderReceivingItems(props.state.recvItems)*/}
        <RecvItemList
          dispatch={dispatch}
          editable={false}
          items={state.savedItems}
          state={state}
        />
      </>)}
      {isPatternCodeEnabled(state) && (<>
        <SmartFormLabel
          style={{ color: GreenColor, fontWeight: 600, fontSize: 18 }}
        >
          Pattern Code
        </SmartFormLabel>
        <SmartScanner
          className="fs-24 mt-2 mb-5 h-50"
          //disabled={isFetching}
          key={`pc-scanner${patternCodeScannerVersion}`}
          //isAutoFocus={autoFocus}
          placeholder="Input pattern code here"
          onChangeValue={onPatternCodeChange}
        />
      </>)}
      <SmartFormLabel
        style={{ color: GreenColor, fontWeight: 600, fontSize: 18 }}
      >
        PO#
      </SmartFormLabel>
      <SmartScannerInput
        allowClear
        autoFocus
        className="fs-24 mt-2 mb-5 h-50"
        placeholder="Input PO# here"
        onClear={onClearPoScanner}
        onScan={onScanPo}
        updateValue={poScannerCode}
        version={poScannerVersion}
      />
      <Row align="middle" justify="space-between">
        <SmartFormLabel
          style={{ color: GreenColor, fontWeight: 600, fontSize: 18 }}
        >
          Location
        </SmartFormLabel>
        {isDefaultLocationRequired && (
          <SuccessButton
            className="icon-btn-small"
            //disabled={!isLocationButtonEnable()}
            onClick={openDefaultLocationDialog}
            size="small"
            type="primary"
          >
            <SettingOutlined />
          </SuccessButton>
        )}
      </Row>
      <SmartScannerInput
        allowClear
        autoFocus
        className="fs-24 mt-2 mb-5 h-50"
        disabled={!isCurrentPoSet()}
        placeholder="Input location here"
        onClear={() => onClearScannedLocation()}
        onScan={onScanLocation}
        updateValue={locationScannerCode}
        version={locationScannerVersion}
      />
      <SmartFormLabel
        style={{ color: GreenColor, fontWeight: 600, fontSize: 18 }}
      >
        Item
      </SmartFormLabel>
      <SmartScannerInput
        allowClear
        autoFocus
        className="fs-24 mt-2 mb-5 h-50"
        disabled={!isItemScannable()}
        placeholder="Input item here"
        onClear={onClearScannedItem}
        onScan={onScanItem}
        updateValue={itemScannerCode}
        version={itemScannerVersion}
      />
      {isLotScannable() && (<>
        <SmartFormLabel
          style={{ color: GreenColor, fontWeight: 600, fontSize: 18 }}
        >
          LOT#
        </SmartFormLabel>
        <Row className="mt-5" justify="space-between">
          <SmartScannerInput
            addonAfter={searchLotButton()}
            allowClear
            className="fs-24 h-50"
            defaultValue={currentLotCode}
            disabled={!isLotReleased()}
            onClear={onClearLotCode}
            onScan={onScanLot}
            placeholder="Input LOT# here"
            style={{width: 'calc(100% - 120px)'}}
            version={lotScannerVersion}
          />
          <SuccessButton
            className="fs-22 h-50 p-4-13"
            disabled={!isLotReleased()}
            onClick={openCreateLotDrawer}
          >
            <PlusOutlined />
          </SuccessButton>
          <Button
            className={currentLotLockClass()}
            onClick={toggleCurrentLotLockState}
          >
            {currentLotLocked ? <LockOutlined /> : <UnlockOutlined />}
          </Button>
        </Row>
      </>)}
      {isUomEnable() && (<>
        <Row align="middle" className="mt-15" justify="space-between">
          <Space>
            <SmartFormLabel
              style={{ color: GreenColor, fontWeight: 600, fontSize: 18 }}
            >
              UOM
            </SmartFormLabel>
            <span className="fs-18 text-grey">
              {`${state.uomInfo.uom}(${state.uomInfo.uomRate})`}
            </span>
          </Space>
          <Button
            className="fs-20 h-48 p-4-12 default-btn-info"
            disabled={!isUomEnable(true)}
            onClick={openUomSelector}
          >
            <SelectOutlined />
          </Button>
        </Row>
      </>)}
      {isItemSubmittable() && (<>
        <SmartFormLabel
          style={{ color: GreenColor, fontWeight: 600, fontSize: 18 }}
        >
          Qty
        </SmartFormLabel>
        <Row className="mt-5" justify="space-between">
          <SmartNumberInput
            className="num-input h-50"
            disabled={!isQtyChangeable()}
            key={`recv-input${itemRecvInputVersion}`}
            min={0}
            onChange={updateCurrentItemRecvQty}
            style={{width: 'calc(100% - 173px)'}}
            value={currentItemRecvQty}
          />
          <Button
            className="fs-16 h-50 p-4-8"
            disabled={!isQtyChangeable(true)}
            onClick={usePoQtyAsCurrentItemRecvQty}
          >
            Use PO Qty
          </Button>
          <Button
            className={currentItemLockClass()}
            onClick={toggleCurrentItemLockState}
          >
            {currentItemLocked ? <LockOutlined /> : <UnlockOutlined />}
          </Button>
        </Row>
        <SmartBlock className="mt-15">{submitItemButton()}</SmartBlock>
      </>)}
    </SmartSpace>
    <SmartSpace>
      <SmartBackButton
        //onClick={() => history.goBack()}
        onClick={onPageBack}
      />
    </SmartSpace>
    {patternCodeSelectorVisible && (
      <PatternCodeSelector
        dispatch={dispatch}
        maskClosable={false}
        onCancel={closePatternCodeSelector}
        onClosePatternCodeSelectorWithoutSelection={onClosePatternCodeSelectorWithoutSelection}
        onSelectPatternCode={onSelectPatternCode}
        state={state}
        title="Select Pattern Code"
        visible={patternCodeSelectorVisible}
      />
    )}
    {defaultLocationDialogVisible && (
      <CodeInputDialog
        maskClosable={false}
        onCancel={closeDefaultLocationDialog}
        onSave={onSetDefaultLocation}
        title="Set Default Location"
        visible={defaultLocationDialogVisible}
      />
    )}
    {defaultWarehouseVisible && (
      <SetDefaultWarehouseDialog
        closable={dialogClosable}
        close={() => {
          setDefaultWarehouseVisible(false);
        }}
        visible={defaultWarehouseVisible}
      />
    )}
    {isCreateLotDrawerOpen && (
      <CreateLotDrawer
        dispatch={dispatch}
        items={[currentItem]}
        onClose={closeCreateLotDrawer}
        onSave={onSelectLot}
        state={state}
        visible={isCreateLotDrawerOpen}
      />
    )}
    {isSearchLotDrawerOpen && (
      <SearchLotDrawer
        dispatch={dispatch}
        onClose={closeSearchLotDrawer}
        onSelect={onSelectLot}
        state={state}
        sku={currentItem.sku}
        visible={isSearchLotDrawerOpen}
      />
    )}
    {uomSelectorVisible && (
      <UomSelector
        bodyStyle={{padding: 10}}
        dispatch={dispatch}
        onCancel={closeUomSelector}
        onSelectUom={onSelectUom}
        refInfo={currentItem}
        state={state}
        title="Select UOM"
        visible={uomSelectorVisible}
      />
    )}
    {state.lockScreen && (
      <ScreenMask>
        <Loading size={LOADING_ICON_SIZE1} />
      </ScreenMask>
    )}
  </>);
};

export default QuickReceive;
